// Utility to manage recently viewed/added products in localStorage
// Stores up to 30 unique items (most recent first), expires after 30 days

export type RecentProduct = {
  slug: string
  product_id?: string
  name?: string
  brand_name?: string
  brand_seo_name?: string
  ean?: string
  image?: string
  base_price?: string
  viewedAt?: number
}

const STORAGE_KEY = 'recentProducts_v2'
const MAX_ITEMS = 30
const EXPIRY_DAYS = 30
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000

function isBrowser() {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

function filterExpired(list: RecentProduct[]): RecentProduct[] {
  const cutoff = Date.now() - EXPIRY_MS
  return list.filter((p) => (p.viewedAt ?? 0) > cutoff)
}

// Une URL S3 presignee (backend storage pharmes3) ne survit pas a son stockage : elle
// expire au bout de `s3_storage.presign_ttl` (900 s cote CS-Cart) et ne vaut que pour le
// backend actif au moment ou elle a ete emise. La memoriser 30 jours donne donc, au mieux
// une vignette morte, au pire — apres bascule du backend vers `file` — un host que
// next/image refuse (« Invalid src prop ... is not configured under images »).
// On ne persiste aucune URL signee, et on purge celles deja presentes en localStorage.
function isPresignedUrl(url?: string): boolean {
  return !!url && url.includes('X-Amz-Signature')
}

export function getRecentProducts(): RecentProduct[] {
  if (!isBrowser()) return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const list: RecentProduct[] = JSON.parse(raw)
    if (!Array.isArray(list)) return []
    return filterExpired(list.filter(x => !!x && typeof x.slug === 'string')).map(p =>
      isPresignedUrl(p.image) ? { ...p, image: undefined } : p
    )
  } catch {
    return []
  }
}

function saveRecentProducts(list: RecentProduct[]) {
  if (!isBrowser()) return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)))
    window.dispatchEvent(new CustomEvent('recentProductsUpdated'))
  } catch {
    // ignore quota errors
  }
}

export function upsertRecentProduct(item: RecentProduct) {
  if (!item?.slug) return
  const current = getRecentProducts()
  const filtered = current.filter(p => p.slug !== item.slug)
  const merged: RecentProduct = {
    ...item,
    viewedAt: Date.now(),
  }
  const next = [merged, ...filtered]
  saveRecentProducts(next)
}

function extractProductImage(product: any): string | undefined {
  const pair = product.image_pairs?.[0] || product.main_pair
  const imagePath =
    pair?.detailed?.image_path ||
    pair?.icon?.image_path ||
    pair?.icons?.iconSmall?.image_path
  if (imagePath) {
    // URL S3 presignee : rien a memoriser, elle sera perimee bien avant d'etre relue.
    if (isPresignedUrl(imagePath)) {
      return undefined
    }
    // URL OVH non signee (bucket public / CDN) : durable, mais ne jamais la reecrire —
    // la regle de repli ci-dessous casserait le chemin (meme garde que getImageCsCart.ts).
    if (imagePath.includes('.io.cloud.ovh.net/')) {
      return imagePath
    }
    if (imagePath.includes('localhost:3008')) {
      return imagePath.replace('localhost:3008', 'd1ihszsyl0wr5t.cloudfront.net')
    }
    return imagePath.replace(/\/[^/]+\/images\//, '/d1ihszsyl0wr5t.cloudfront.net/images/')
  }
  return product.image_url || product.image || product.default_image_url || undefined
}

export function recentViewProduct(product: any) {
  if (!product) return
  upsertRecentProduct({
    slug:
      typeof product.seo_name === 'string'
        ? product.seo_name
        : String(
            product?.seo_name?.seo_name ?? product?.seo_name?.value ?? product?.seo_name ?? ''
          ),
    product_id: product.product_id ? String(product.product_id) : undefined,
    name: product.product,
    brand_name: product.brand_name,
    brand_seo_name: product.brand_seo_name,
    ean: product.product_code,
    image: extractProductImage(product),
    base_price: product.base_price || undefined,
  })
}

export function recentAddToCart(product: any) {
  recentViewProduct(product)
}

export function removeRecentProduct(slug: string) {
  if (!slug) return
  const current = getRecentProducts()
  const next = current.filter(p => p.slug !== slug)
  saveRecentProducts(next)
}
